import * as React from "react";
import { IconButton, List as MList } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import ActivityDetailWithoutLink from "../Dashboard/ActivityDetailWithoutLink";
import ThriveTitle from "./ThriveTitle";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { StyledButton, StyledButtonMain } from "./Buttons";
import SearchIcon from "@mui/icons-material/Search";
import ActivityDetail from "../Dashboard/ActivityDetail";
import { FixedSizeList as List } from "react-window";
import { axiosInstance, fetchInstance } from "../../utils/networking";
import Fuse from "fuse.js";
import { UserContext } from "../../context/UserContext";
import { UserType } from "../../interfaces";
import { useLocation } from "react-router-dom";
interface Props {
  title: string;
  cases: any[];
  checkedCases: string[];
  setCheckedCases: any;
  selectedCase: any;
  setSelectedCase: any;
  enableControl: boolean;
  enableSelection: boolean;
  enableMultiSelection: boolean;
  status_default: string;
  sortby_default: string;
  period_default: string; // Optional prop for period default
  setOpen: any;
  handleNext: any;
  height: number | string;
  setEventEmailTos?: any; // Optional prop for event email tos
  reloadCases: boolean; // Optional prop to reload cases
}

const ThriveCaseSelection: React.FC<Props> = ({
  title,
  cases,
  checkedCases,
  setCheckedCases,
  selectedCase,
  setSelectedCase,
  enableControl,
  enableSelection,
  enableMultiSelection,
  status_default,
  sortby_default,
  period_default,
  setOpen,
  handleNext,
  height,
  setEventEmailTos,
  reloadCases,
}: Props) => {
  const location = useLocation();

  const statusMap: { [key: string]: string } = {
    open: "Open",
    completed: "Closed",
    either: "Either",
  };
  const [originalCases, setOriginalCases] = React.useState<any[]>(cases);
  const [currentCases, setCurrentCases] = React.useState<any[]>(cases);
  const [sortby, setSortby] = React.useState<string>(sortby_default);
  const [status, setStatus] = React.useState<string>(status_default);
  const [period, setPeriod] = React.useState<string>(period_default);
  const [primaryIndustry, setPrimaryIndustry] = React.useState<string>("all");
  const [sbc, setSbc] = React.useState<string>("all");
  const [search, setSearch] = React.useState<string>("");
  const [loadingFlag, setLoadingFlag] = React.useState<boolean>(true);
  const [searchFlag, setSearchFlag] = React.useState<boolean>(false);
  // const fullyChecked = Array.from(Array(currentCases.length).keys());
  const { user } = React.useContext(UserContext);
  const [availableUsers, setAvailableUsers] = React.useState<any[]>([]);

  // Sync currentCases and originalCases with cases prop when it changes, unless loading or search is active
  React.useEffect(() => {
    if (!reloadCases) {
      setCurrentCases(cases);
      setOriginalCases(cases);
    }
  }, [cases]);

  React.useEffect(() => {
    const fetchCasesWithFirms = async () => {
      let stream_cases: any[] = [];
      const response = await fetchInstance(
        `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
        {
          method: "POST",
          body: JSON.stringify({
            period: period,
            caseStatus: status,
            sortBy: sortby,
            primaryIndustry: primaryIndustry,
            ids: [],
            sbc: sbc,
          }), // Adjust the limit as needed
        }
      );
      if (!response.body) {
        throw new Error("ReadableStream not supported in this environment.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete line

        for (const line of lines) {
          if (line.trim()) {
            try {
              const obj = JSON.parse(line);
              stream_cases.push(obj);
            } catch (err) {
              console.error("Failed to parse line:", line, err);
            }
          }
        }
      }

      //Filter out the current case if we are on the case page to avoid merging with itself
      if (location.pathname === "/case") {
        const currentCaseId = location.search.split("id=")[1];
        stream_cases = stream_cases.filter((c) => c._id !== currentCaseId);
      }

      setCurrentCases(stream_cases);
      setOriginalCases(stream_cases);
      setLoadingFlag(false);
    };
    // console.log(
    //   "Fetching cases with status:",
    //   status,
    //   "and sortby:",
    //   sortby,
    //   "period:",
    //   period,
    //   "primaryIndustry:",
    //   primaryIndustry
    // );
    // console.log("Reloading cases:", reloadCases);
    if (reloadCases) {
      setCurrentCases([]);
      setLoadingFlag(true);
      fetchCasesWithFirms();
    } else {
      setLoadingFlag(false);
      return;
    }
  }, [status, sortby, period, primaryIndustry, reloadCases, sbc]);

  React.useEffect(() => {
    if (user.role === "admin") {
      axiosInstance.get(`/user/allUsers`).then((res) => {
        const users = res.data.users as UserType[];
        const userNames = users.map((user: any) => {
          return {
            label: user.firstName + " " + user.lastName,
            id: user._id,
          };
        });
        setAvailableUsers(
          userNames.sort((a, b) =>
            a.label > b.label ? 1 : b.label > a.label ? -1 : 0
          )
        );
      });
    }
  }, []);

  const handleToggle = (value: string) => () => {
    const currentIndex = checkedCases.indexOf(value);
    const newChecked = [...checkedCases];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedCases(newChecked);
    if (setEventEmailTos) {
      const selectedFirms = originalCases
        .filter((c) => newChecked.includes(c._id))
        .map((c) => c["firmRecord"]["_id"]);
      setEventEmailTos(selectedFirms);
    }
  };

  const handleToggleAll = () => {
    const visibleIds = currentCases.map((c) => c._id);
    const visibleSelectedCount = visibleIds.filter((id) =>
      checkedCases.includes(id)
    ).length;

    let newChecked: string[];

    if (visibleSelectedCount < visibleIds.length) {
      // Select all visible (union)
      const existingHidden = checkedCases.filter(
        (id) => !visibleIds.includes(id)
      );
      newChecked = [...existingHidden, ...visibleIds];
    } else {
      // Deselect all visible
      newChecked = checkedCases.filter((id) => !visibleIds.includes(id));
    }
    setCheckedCases(newChecked);

    if (setEventEmailTos) {
      const selectedFirms = originalCases
        .filter((c) => newChecked.includes(c._id))
        .map((c) => c["firmRecord"]["_id"]);
      setEventEmailTos(selectedFirms);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleExport = () => {
    let csvContent: any[] = [];
    const csvHeading = [
      "thriveID",
      "Company Name",
      "Contact",
      "Contact First Name",
      "Contact Last Name",
      "Email",
      "Phone",
      "Website",
      "Address",
      "City",
      "ZIP",
      "Founding Year",
      "Company Narrative",
      "primaryIndustry",
      "secondaryIndustry",
      "Annual Revenue",
      "Employees",
      "Woman-Owned",
      "Veteran-Owned",
      "Minority-Owned",
      "firmScore",
      "totalGrantsValue",
      "Home-based",
      "Selling Online",
      "In-Person Sales",
      "Commercial Location",
      "Mobile Business",
      "Case Status",
      "Created Date",
      "Last Activity Date",
      "Primary SBC",
    ];
    csvContent.push(csvHeading);

    for (const c of currentCases) {
      const firm = c.firmRecord ?? {};
      const contact = firm["Contact"] ? String(firm["Contact"]) : "";
      const hasSpace = contact.includes(" ");
      const firstName = hasSpace ? contact.split(" ")[0] : contact;
      const lastName = hasSpace ? contact.split(" ").slice(-1)[0] : contact;
      const csvRow = [];
      for (const key of csvHeading) {
        if (key === "thriveID") csvRow.push(firm["_id"] ?? "");
        else if (key === "Contact First Name") csvRow.push(firstName);
        else if (key === "Contact Last Name") csvRow.push(lastName);
        else if (key === "Case Status") csvRow.push(statusMap[c.status] ?? c.status);
        else if (key === "Created Date") csvRow.push(c.date ? new Date(c.date).toLocaleDateString() : "");
        else if (key === "Last Activity Date") csvRow.push(c.lastActivityDate ? new Date(c.lastActivityDate).toLocaleDateString() : "");
        else if (key === "Primary SBC") csvRow.push(c.primarySbc ?? c.createdBy ?? "");
        else csvRow.push(firm[key] ?? "");
      }
      csvContent.push(csvRow);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cases_export.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "cases",
      record_count: currentCases.length,
      filters: { status, period, primaryIndustry, sbc, search },
    });
  };

  const handleSearch = () => {
    if (search.trim() !== "") {
      // console.log("Search with:", search);
      const options = {
        includeScore: true,
        keys: [
          { name: "firmRecord.Company Name", weight: 0.5 },
          { name: "firmRecord.Contact", weight: 0.5 },
        ],
        threshold: 0.5,
        distance: 100, // raise for more tolerance on position
        ignoreLocation: true, // makes short/typo'd queries match better
        minMatchCharLength: 2,
      };

      const fuse = new Fuse(currentCases, options);
      const result = fuse.search(search);
      const matchedCases = result.map((res) => res.item);
      setCurrentCases(matchedCases);
      setSearchFlag(true);
    }
  };

  const Row: React.FC<{ index: number; style: React.CSSProperties }> = ({
    index,
    style,
  }) => {
    const caseId = currentCases[index]["_id"];
    const labelId = `checkbox-list-secondary-label-${caseId}`;
    const caseItem = currentCases[index];
    const firm = caseItem["firmRecord"];

    return (
      <ListItem key={index} disablePadding sx={style}>
        {enableSelection && enableMultiSelection ? (
          <Checkbox
            edge="end"
            onChange={handleToggle(caseId)}
            checked={checkedCases.indexOf(caseId) !== -1}
            inputProps={{ "aria-labelledby": labelId }}
          />
        ) : (
          <></>
        )}

        <ListItemButton>
          {enableSelection ? (
            <ActivityDetailWithoutLink
              key={labelId}
              case={caseItem}
              firm={firm}
              selectedCase={enableMultiSelection ? checkedCases : selectedCase}
              onSelect={
                enableMultiSelection
                  ? handleToggle(caseItem["_id"])
                  : () => setSelectedCase(caseItem)
              }
              index={firm["_id"]}
              index2={index}
              multiselect={enableMultiSelection}
            />
          ) : (
            <ActivityDetail
              key={labelId}
              case={caseItem}
              showStatus={statusMap[caseItem.status]}
              firm={firm}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  // console.log("Current Cases: ", currentCases);
  // console.log("Available Users: ", availableUsers);
  return (
    <>
      {title ? (
        <ThriveTitle title={title} setOpen={setOpen}></ThriveTitle>
      ) : (
        <></>
      )}

      <MList
        dense
        sx={{
          width: "100%",
          margin: "auto",
          bgcolor: "white",
          overflow: "auto",
          // height: "85%",
        }}
      >
        {enableControl ? (
          <ListItem>
            <Stack direction={"row"} spacing={1}>
              <FormControl sx={{ width: "100px" }}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                  }}
                >
                  <MenuItem value={"either"}>Either</MenuItem>
                  <MenuItem value={"open"}>Open</MenuItem>
                  <MenuItem value={"completed"}>Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: "100px" }}>
                <InputLabel id="sort-label">Sort By</InputLabel>
                <Select
                  labelId="sort-label"
                  value={sortby}
                  label="Sort By"
                  onChange={(e: any) => {
                    setSortby(e.target.value);
                  }}
                >
                  <MenuItem value={"recent"}>Recent</MenuItem>
                  <MenuItem value={"name"}>Name</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: "200px" }}>
                <InputLabel id="period-label">Period</InputLabel>
                <Select
                  labelId="period-label"
                  value={period}
                  label="Period"
                  onChange={(e: any) => {
                    setPeriod(e.target.value);
                  }}
                >
                  <MenuItem value={"1d"}>1 Day</MenuItem>
                  <MenuItem value={"1w"}>1 Week</MenuItem>
                  <MenuItem value={"2w"}>2 Weeks</MenuItem>
                  <MenuItem value={"1m"}>1 Month</MenuItem>
                  <MenuItem value={"2m"}>2 Months</MenuItem>
                  <MenuItem value={"all"}>All</MenuItem>
                </Select>
              </FormControl>
              <TextField
                sx={{ width: "300px" }}
                label="Search Company..."
                variant="outlined"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                value={search}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="search"
                      edge="end"
                      onClick={handleSearch}
                    >
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
              {searchFlag ? (
                <StyledButtonMain
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setCurrentCases(originalCases);
                    setSearch("");
                    setSearchFlag(false);
                  }}
                >
                  Clear Search
                </StyledButtonMain>
              ) : (
                <></>
              )}
            </Stack>
          </ListItem>
        ) : (
          <> </>
        )}

        {enableControl ? (
          <ListItem>
            <Stack direction={"row"} spacing={1}>
              <FormControl sx={{ width: "300px" }}>
                <InputLabel id="primary-industry-label">
                  Primary Industry
                </InputLabel>
                <Select
                  labelId="primary-industry-label"
                  value={primaryIndustry}
                  label="Primary Industry"
                  onChange={(e) => {
                    setPrimaryIndustry(e.target.value);
                  }}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem
                    value={
                      "Accommodations - Bed and breakfast, or any other lodging facility"
                    }
                  >
                    Accommodations - Bed and breakfast, or any other lodging
                    facility
                  </MenuItem>
                  <MenuItem value={"Business Services"}>
                    Business Services
                  </MenuItem>
                  <MenuItem
                    value={
                      "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services"
                    }
                  >
                    Construction and Maintenance Services - Grading, excavating,
                    other construction and maintenance services
                  </MenuItem>
                  <MenuItem value={"Engineering, Design and Development"}>
                    Engineering, Design and Development
                  </MenuItem>
                  <MenuItem value={"Entertainment and Recreation"}>
                    Entertainment and Recreation
                  </MenuItem>
                  <MenuItem value={"Food"}>Food</MenuItem>
                  <MenuItem
                    value={"Healthcare - chiropractors, dental, health clinics"}
                  >
                    Healthcare - chiropractors, dental, health clinics
                  </MenuItem>
                  <MenuItem
                    value={
                      "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance"
                    }
                  >
                    Maintenance & Repair - automobile repair, automobile
                    dealership, cellular phone repair, landscaping and lawn
                    maintenance
                  </MenuItem>
                  <MenuItem
                    value={
                      "Nonprofit - churches, religious centers, advocacy organizations, etc."
                    }
                  >
                    Nonprofit - churches, religious centers, advocacy
                    organizations, etc.
                  </MenuItem>
                  <MenuItem value={"Other"}>Other</MenuItem>
                  <MenuItem value={"Manufacturing"}>Manufacturing</MenuItem>
                  <MenuItem value={"Personal Services"}>
                    Personal Services
                  </MenuItem>
                  <MenuItem
                    value={
                      "Professional & Other Services - taxes, consulting, publishing, marketing"
                    }
                  >
                    Professional & Other Services - taxes, consulting,
                    publishing, marketing
                  </MenuItem>
                  <MenuItem value={"Retail"}>Retail</MenuItem>
                  <MenuItem value={"Technology"}>Technology</MenuItem>
                  <MenuItem value={"Transportation - limousine services, etc."}>
                    Transportation - limousine services, etc.
                  </MenuItem>
                </Select>
              </FormControl>
              {user.role === "admin" && (
                <FormControl sx={{ width: "200px" }}>
                  <InputLabel id="sbc-label">SBC</InputLabel>
                  <Select
                    labelId="sbc-label"
                    value={sbc}
                    label="SBC"
                    onChange={(e: any) => {
                      setSbc(e.target.value);
                    }}
                  >
                    <MenuItem value={"all"}>All</MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton
                onClick={handleExport}
                title="Export to CSV"
                disabled={currentCases.length === 0}
              >
                <FileDownloadIcon />
              </IconButton>
            </Stack>
          </ListItem>
        ) : (
          <> </>
        )}

        <ListItem disablePadding>
          {enableSelection && enableMultiSelection ? (
            <>
              <Checkbox
                edge="end"
                onChange={handleToggleAll}
                indeterminate={
                  currentCases.some((c) => checkedCases.includes(c._id)) &&
                  !currentCases.every((c) => checkedCases.includes(c._id))
                }
                checked={
                  currentCases.length > 0 &&
                  currentCases.every((c) => checkedCases.includes(c._id))
                }
              />
              <ListItemButton>
                {currentCases.length > 0 &&
                  currentCases.every((c) => checkedCases.includes(c._id))
                  ? "DESELECT ALL"
                  : "SELECT ALL"}
              </ListItemButton>
            </>
          ) : (
            <></>
          )}
        </ListItem>
      </MList>
      {loadingFlag ? (
        <Paper sx={{ textAlign: "center" }}>
          <CircularProgress />
        </Paper>
      ) : (
        <>
          {currentCases.length === 0 ? (
            <Paper sx={{ width: "100%", textAlign: "center", height: "80%" }}>
              <Typography>No Matching Case</Typography>
            </Paper>
          ) : (
            <List
              height={height}
              width={"100%"}
              itemCount={currentCases.length}
              itemSize={170} // height of each item
            >
              {Row}
            </List>
          )}
        </>
      )}

      {enableSelection ? (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <StyledButton
            variant="contained"
            sx={{
              width: "200px",
              fontSize: "20px",
              backgroundColor: "#9E1F63FF",
              marginTop: "20px",
            }}
            onClick={handleNext}
            disabled={checkedCases.length === 0 && selectedCase._id === ""}
          >
            Next
          </StyledButton>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

export default ThriveCaseSelection;
